/**
 * Note metadata snapshot — editable fields persisted on QuestionnaireResponse.
 */
import { z } from 'zod';

export const noteMetadataModalityValues = ['in-person', 'telehealth', 'hybrid'] as const;
export type NoteMetadataModality = (typeof noteMetadataModalityValues)[number];

export const noteMetadataPatientStatusValues = ['new', 'established'] as const;
export type NoteMetadataPatientStatus = (typeof noteMetadataPatientStatusValues)[number];

export const noteMetadataMdmLevelValues = [
  'straightforward',
  'low',
  'moderate',
  'high',
] as const;
export type NoteMetadataMdmLevel = (typeof noteMetadataMdmLevelValues)[number];

export const noteMetadataPayerPlanTypeValues = [
  'Medi-Cal',
  'Commercial',
  'Medicare',
  'Other',
] as const;
export type NoteMetadataPayerPlanType = (typeof noteMetadataPayerPlanTypeValues)[number];

export const noteMetadataProviderSchema = z.object({
  reference: z.string().optional(),
  display: z.string().min(1),
});

export const noteMetadataBillingCodeSchema = z.object({
  code: z.string().min(1),
  display: z.string().optional(),
  chargeItemId: z.string().optional(),
  /** Units for this service row (defaults to 1 in the editor). */
  units: z.number().positive().optional(),
  /** HealthcareService catalog id when selected from configured service types. */
  healthcareServiceId: z.string().optional(),
});

const episodeSchema = z.object({
  reference: z.string().optional(),
  display: z.string().optional(),
});

export const encounterScrubberDecisionValues = ['accept', 'reject'] as const;
export type EncounterScrubberDecision = (typeof encounterScrubberDecisionValues)[number];

export const encounterScrubberReviewItemSchema = z.object({
  code: z.string().min(1),
  action: z.string().min(1),
  message: z.string().min(1),
  decision: z.enum(encounterScrubberDecisionValues),
  suggestedCode: z.string().optional(),
  units: z.number().positive().optional(),
});

export const encounterScrubberReviewSchema = z.object({
  reviewedAt: z.string().min(1),
  items: z.array(encounterScrubberReviewItemSchema),
});

export type EncounterScrubberReview = z.infer<typeof encounterScrubberReviewSchema>;
export type EncounterScrubberReviewItem = z.infer<typeof encounterScrubberReviewItemSchema>;

/** Raw shape including legacy `location` string for migration. */
const noteMetadataSnapshotRawSchema = z.object({
  start: z.string().min(1),
  end: z.string().optional(),
  /** Session length in minutes; telehealth may use one decimal from Zoom timing. */
  durationMinutes: z.number().min(0).max(480).optional(),
  attendance: z.string().optional(),
  modality: z.enum(noteMetadataModalityValues).optional(),
  /** @deprecated Use `locations` — migrated on parse. */
  location: z.string().optional(),
  locations: z.array(z.string().min(1)).optional(),
  renderingProviders: z.array(noteMetadataProviderSchema).optional(),
  episodeOfCare: episodeSchema.optional(),
  billingCodes: z.array(noteMetadataBillingCodeSchema).optional(),
  units: z.number().positive().optional(),
  capturedAt: z.string().optional(),
  /** Payer-allowed CPT codes for add-on evaluation (e.g. 96127). */
  payerCodesList: z.array(z.string().min(1)).optional(),
  /** E/M and billing scrubber fields — required conditionally at save. */
  patientStatus: z.enum(noteMetadataPatientStatusValues).optional(),
  mdmLevel: z.enum(noteMetadataMdmLevelValues).optional(),
  firstSessionWithClientOrCase: z.boolean().optional(),
  newTherapistFirstSessionWithClientOrCase: z.boolean().optional(),
  diagnosticEvaluationDocumentationPresent: z.boolean().optional(),
  crisisDocumentationPresent: z.boolean().optional(),
  patientGroupEncounterAttended: z.boolean().optional(),
  assessmentPerformedSameDateAsSession: z.boolean().optional(),
  psychotherapyAddOn: z.boolean().optional(),
  interactiveComplexityFactorDocumented: z.boolean().optional(),
  payerPlanType: z.enum(noteMetadataPayerPlanTypeValues).optional(),
  payerName: z.string().optional(),
  prior96146CountForClient: z.number().min(0).optional(),
  legacyConsultCodeEnabled: z.boolean().optional(),
  payerAllowsConsultCodes: z.boolean().optional(),
  /** Provider accept/reject decisions for pre-save encounter scrubber review. */
  encounterScrubberReview: encounterScrubberReviewSchema.optional(),
});

/** Plain object schema for Mantine field validation (no transform — zodResolver breaks on effects). */
export const noteMetadataSnapshotFormSchema = noteMetadataSnapshotRawSchema;

export const noteMetadataSnapshotSchema = noteMetadataSnapshotRawSchema.transform((data) => {
  const locations =
    data.locations ??
    (data.location?.trim() ? [data.location.trim()] : undefined);
  const { location: _legacy, ...rest } = data;
  return { ...rest, locations };
});

export type NoteMetadataSnapshot = z.infer<typeof noteMetadataSnapshotSchema>;
export type NoteMetadataProvider = z.infer<typeof noteMetadataProviderSchema>;
export type NoteMetadataBillingCode = z.infer<typeof noteMetadataBillingCodeSchema>;
